import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  TemplateRef,
  Type,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  AlertComponent,
  CardComponent,
  DisabledGranularity,
  MapComponent,
  MapGeocoder,
  MapLayerDescriptor,
  MessengerService,
  PeriodDisplayComponent,
  PeriodPickerComponent,
  PeriodPrevNextComponent,
  PeriodService,
  RoutingService,
  Str,
  TimeShortcut,
  TimeUnit,
  __,
  buildClusterCircleStyle,
  grey,
  pastelGreen,
  red,
} from '@web/birdz-angular';
import {
  CardKpiMapPopupComponent,
  CardKpiMapPopupContext,
} from 'app/components/card-kpi-map/card-kpi-map-popup.component';
import { ModalConfirmHeavyMapLoadingComponent } from 'app/components/modal-confirm-heavy-map-loading/modal-confirm-heavy-map-loading.component';
import { ModalHelpContractConfigComponent } from 'app/components/modal-help-contract-config/modal-help-contract-config.component';
import { Filter } from 'app/interfaces/filter.interface';
import { KpiMapLegendItem } from 'app/interfaces/kpi-map-legend-item.interface';
import {
  getKpiColor,
  getKpiLegendItemId,
  KPI_LEGEND_COLORS,
  KpiLegendItemId,
} from 'app/support/kpi-map-legend.support';
import { Kpi, hasNumericValues, isKpiDisplayableForGranularity } from 'app/interfaces/kpi.interface';
import { MapResponse } from 'app/interfaces/map-response.interface';
import { PdsLight } from 'app/interfaces/pds-light.interface';
import { Point } from 'app/interfaces/point.interface';
import { Api } from 'app/services/api.service';
import { FilterService } from 'app/services/filter.service';
import { KpiPeriodService } from 'app/services/kpi-period.service';
import { UserService } from 'app/services/user.service';
import Feature from 'ol/Feature';
import OlPoint from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { StyleFunction } from 'ol/style/Style';
import {
  EMPTY,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  skip,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-card-kpi-map',
  providers: [{ provide: PeriodService, useExisting: KpiPeriodService }],
  imports: [
    CardComponent,
    TranslocoPipe,
    AsyncPipe,
    AlertComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MapComponent,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    PeriodPrevNextComponent,
    PeriodPickerComponent,
    PeriodDisplayComponent,
  ],
  templateUrl: './card-kpi-map.component.html',
  styleUrl: './card-kpi-map.component.scss',
})
export class CardKpiMapComponent implements OnInit, AfterViewInit {
  private filterService = inject(FilterService);
  private api = inject(Api);
  private messenger = inject(MessengerService);
  private userService = inject(UserService);
  private transloco = inject(TranslocoService);
  private dialog = inject(MatDialog);
  private routing = inject(RoutingService);
  private ref = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private periodService = inject(KpiPeriodService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(MapComponent) private mapComponent?: MapComponent;
  @ViewChild('meterOptionTpl') meterOptionTpl!: TemplateRef<{ $implicit: PdsLight }>;

  hasPerimeter$: Observable<boolean>;
  techno$: Observable<string | null>;
  isBirdzAdmin$!: Observable<boolean>;

  searchVersion = 0;
  extraGeocoders: MapGeocoder[] = [];
  meterGeocoder!: MapGeocoder<PdsLight>;
  selectedPdsId?: string | null;
  kpis: Kpi[] = [];
  kpiSearchFunction: (search: string, option: Kpi) => boolean = (search, option) => Str.search(search, option.label);
  selectedKpi?: string;
  getSelectedKpi: () => Kpi = () => this.kpis.find((kpi) => this.selectedKpi === kpi.id)!;
  isSelectedKpiDisplayable: () => boolean = () => {
    const kpi = this.getSelectedKpi();
    const granularity = this.periodService.granularity;
    return isKpiDisplayableForGranularity(kpi, granularity);
  };
  granularities: TimeUnit[] = ['day', 'week', 'month'];
  disabledGranularities: DisabledGranularity[] = [];
  isGranularityDisabled = (granularity: TimeUnit) =>
    this.disabledGranularities.some(
      (disabled) => disabled === granularity || (typeof disabled === 'object' && disabled?.granularity === granularity)
    );
  undisplayableSelectedKpiMessage: string | null = null;
  shortcuts: TimeShortcut[] = [
    'yesterday',
    'the_day_before_yesterday',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
  ];
  disabledShortcuts: TimeShortcut[] = [];
  heavyMapThreshold = 100000; // number of pds above which the map is considered heavy
  isHeavy = false;
  isLoaded = false;
  isLoading = 0;
  hasNonGeocodedPds = false;

  mapLegend: KpiMapLegendItem[] = [];
  missingThresholdAlert = false;

  private allPoints: Point[] = [];
  mapSource = new VectorSource<Feature>({ features: [] });
  popupComponent: Type<CardKpiMapPopupComponent> = CardKpiMapPopupComponent;

  get popupContext(): CardKpiMapPopupContext {
    return {
      kpis: this.kpis,
      selectedKpi: this.selectedKpi,
      from: this.periodService.from,
      isBirdzAdmin: this.userService.isBirdzAdmin(),
    };
  }

  private readonly pointStyleFn: StyleFunction = (feature) => {
    const color = (feature.get('color') as string) ?? grey;
    return new Style({
      image: new Circle({
        radius: 5,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#00000077', width: 1 }),
      }),
    });
  };

  private readonly spiderfyStyleCache = new Map<string, Style>();

  private getDominantColor(features: Feature[]): string {
    const colorCounts: Record<string, number> = {};
    for (const f of features) {
      const c = (f.get('color') as string) ?? grey;
      colorCounts[c] = (colorCounts[c] ?? 0) + 1;
    }
    const maxCount = Math.max(...Object.values(colorCounts));
    return Object.entries(colorCounts).find(([, c]) => c === maxCount)?.[0] ?? grey;
  }

  private buildClusterStyle(feature: Feature): ReturnType<StyleFunction> {
    const features: Feature[] = feature.get('features') ?? [];
    if (features.length <= 1) {
      return undefined;
    }
    return buildClusterCircleStyle(this.getDominantColor(features), features.length);
  }

  private buildSpiderfyStyle(feature: Feature): Style | undefined {
    const features: Feature[] = feature.get('features') ?? [];
    if (!features.length) {
      return undefined;
    }

    const color = this.getDominantColor(features);
    if (!this.spiderfyStyleCache.has(color)) {
      this.spiderfyStyleCache.set(
        color,
        new Style({
          image: new Circle({
            radius: 5,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#00000077', width: 1 }),
          }),
        })
      );
    }
    return this.spiderfyStyleCache.get(color);
  }

  mapLayers: MapLayerDescriptor[] = [
    {
      id: 'kpi-pds',
      source: this.mapSource,
      pointStyle: this.pointStyleFn,
      clustering: { style: (f) => this.buildClusterStyle(f as Feature) },
      ungroupedPointStyle: this.pointStyleFn,
      spiderfy: { style: (f) => this.buildSpiderfyStyle(f as Feature) },
    },
  ];

  getKpiColor = getKpiColor;
  getKpiLegendItemId = getKpiLegendItemId;

  getMapLegend: (kpi: Kpi) => KpiMapLegendItem[] = (kpi: Kpi) => {
    const legendItem = (id: KpiLegendItemId, label: string) => ({
      id,
      label,
      color: KPI_LEGEND_COLORS[id].color,
      toggled: true,
    });

    if (kpi.technology === 'KAPTA') {
      if (kpi.type === 'NB_JOURS') {
        return [
          legendItem('isCompliant', __('KPI_MAP_LEGEND.KAPTA.NB_DAYS.IS_COMPLIANT')),
          legendItem('isNotCompliant', __('KPI_MAP_LEGEND.KAPTA.NB_DAYS.IS_NOT_COMPLIANT')),
        ];
      }
      if (kpi.threshold === null) {
        return [];
      }
      const timeframe = kpi.ytd_display ? 'YTD' : 'DAILY';
      return [
        legendItem('ratioAboveThreshold', __(`KPI_MAP_LEGEND.KAPTA.RATIO.${timeframe}.ABOVE_THRESHOLD`)),
        legendItem('ratioUnderThreshold', __(`KPI_MAP_LEGEND.KAPTA.RATIO.${timeframe}.UNDER_THRESHOLD`)),
      ];
    }

    if (kpi.technology === 'SEWERIN') {
      if (kpi.ytd_display) {
        if (kpi.type === 'NB_JOURS') {
          return [
            {
              id: 'isCompliant',
              label: __('KPI_MAP_LEGEND.SEWERIN.NB_DAYS.IS_COMPLIANT'),
              color: pastelGreen,
              toggled: true,
            },
            {
              id: 'isNotCompliant',
              label: __('KPI_MAP_LEGEND.SEWERIN.NB_DAYS.IS_NOT_COMPLIANT'),
              color: red,
              toggled: true,
            },
          ];
        } else if (kpi.type === 'RATIO') {
          if (kpi.threshold === null) {
            return [];
          }
          return [
            {
              id: 'ratioAboveThreshold',
              label: __('KPI_MAP_LEGEND.SEWERIN.RATIO.ABOVE_THRESHOLD'),
              color: pastelGreen,
              toggled: true,
            },
            {
              id: 'ratioUnderThreshold',
              label: __('KPI_MAP_LEGEND.SEWERIN.RATIO.UNDER_THRESHOLD'),
              color: red,
              toggled: true,
            },
          ];
        } else {
          return [];
        }
      }

      return [
        { id: 'stateOk', label: __('KPI_MAP_LEGEND.SEWERIN.STATE.OK'), color: pastelGreen, toggled: true },
        { id: 'stateKo', label: __('KPI_MAP_LEGEND.SEWERIN.STATE.KO'), color: red, toggled: true },
      ];
    }

    return [
      { id: 'stateOk', label: __('KPI_MAP_LEGEND.WATER.STATE.OK'), color: pastelGreen, toggled: true },
      { id: 'stateKo', label: __('KPI_MAP_LEGEND.WATER.STATE.KO'), color: red, toggled: true },
    ];
  };

  constructor() {
    this.hasPerimeter$ = this.filterService.hasPerimeter$;
    this.techno$ = this.filterService.filter$.pipe(map((f: Filter) => f.techno));
    this.isBirdzAdmin$ = this.userService.user$.pipe(map(() => this.userService.isBirdzAdmin()));
  }

  ngOnInit() {
    this.clearSelectionOnFilterChange();
    this.fetchContractKpis();
    this.fetchMapOnDateChange();
  }

  ngAfterViewInit(): void {
    this.meterGeocoder = {
      id: 'meter',
      ctaLabel: __('GEOCODER_CTA'),
      label: __('GEOCODER_LABEL'),
      icon: 'readiness_score',
      minLength: 3,
      search: (q) => this.api.search(this.filterService.getPerimeter(), q, this.periodService.to ?? undefined),
      displayFn: (opt: PdsLight) => opt[opt.found_by ?? 'equipment_id'] ?? opt.equipment_id ?? opt.id,
      optionTemplate: this.meterOptionTpl,
      onSelect: (opt: PdsLight) => {
        this.selectedPdsId = opt.id;
        this.mapComponent?.selectFeatureById(opt.id);
      },
    };
    this.extraGeocoders = [this.meterGeocoder];
    this.cdr.detectChanges();
  }

  fetchContractKpis() {
    combineLatest([this.filterService.filter$, this.transloco.langChanges$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        filter(() => (this.filterService.getFilter().contract ?? null) !== null),
        tap(() => (this.kpis = [])),
        tap(() => this.isLoading++),
        switchMap(() =>
          this.api
            .fetchContractKpis(
              this.filterService.getContract() as string,
              this.filterService.getTechno() as string,
              true
            )
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              tap((kpis: Kpi[]) => {
                const showRawKpis = this.userService.getShowRawKpis();
                this.kpis = kpis
                  .filter((kpi) => !kpi.admin_birdz_display || this.userService.isBirdzAdmin())
                  .filter((kpi) => !kpi.raw_display || showRawKpis)
                  .sort(Str.sortBy('label'));
                // if a kpi is already selected, keep it selected as long as it exists
                // else find the first daily kpi (if none, take the first from the whole kpi list)
                this.selectedKpi = kpis.find((kpi) => kpi.id === this.selectedKpi)
                  ? this.selectedKpi
                  : (kpis.find((kpi) => kpi.daily_display)?.id ?? kpis.at(0)?.id);
                this.updateLegend();
                this.updateGranularities();
                this.periodService.setDefaultView();
                this.updateSelectedKpiAlert();
              }),
              filter((kpis: Kpi[]) => kpis.length > 0),
              tap(() => this.fetchMap(true)),
              this.messenger.showFriendlyErrorMessage(EMPTY),
              finalize(() => this.isLoading--)
            )
        )
      )
      .subscribe();
  }

  fetchMapOnDateChange() {
    this.periodService.period$
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap(() => {
          this.mapComponent?.closePopup();
          this.updateSelectedKpiAlert();
          this.fetchMap();
        })
      )
      .subscribe();
  }

  updateGranularities() {
    // disable granularities for which there are no displayable KPIs
    this.disabledGranularities = this.granularities
      .filter(
        (granularity) => this.getSelectedKpi() && !isKpiDisplayableForGranularity(this.getSelectedKpi(), granularity)
      )
      .map((granularity) => {
        const tooltip = __('SELECTED_KPI_IS_NOT_DISPLAYABLE.' + granularity.toUpperCase());
        return { granularity, tooltip };
      });
  }

  updateSelectedKpiAlert() {
    this.undisplayableSelectedKpiMessage = null;

    if (!this.isSelectedKpiDisplayable()) {
      const granularity = this.periodService.granularity;
      this.undisplayableSelectedKpiMessage = __(
        'SELECTED_KPI_IS_NOT_DISPLAYABLE_CHANGE_DATE.' + granularity.toUpperCase(),
        { kpi: this.getSelectedKpi().label }
      );
    }
  }

  updateLegend() {
    const kpi = this.getSelectedKpi();
    this.mapLegend = this.getMapLegend(kpi);
    this.missingThresholdAlert = hasNumericValues(kpi) && kpi.type === 'RATIO' && kpi.threshold === null;
  }

  onKpiSelectionChange(_: MatSelectChange) {
    this.mapComponent?.closePopup();
    this.updateLegend();
    this.updateGranularities();
    this.updateSelectedKpiAlert();
    this.fetchMap();
  }

  private pointColor(point: Point): string {
    if (!this.selectedKpi) {
      return grey;
    }
    const kpi = this.getSelectedKpi();
    if (!kpi) {
      return grey;
    }
    const kpiValue = point.kpis ? point.kpis[this.selectedKpi] : null;
    return this.getKpiColor(kpi, kpiValue ?? null);
  }

  private rebuildSource(shouldFit = false) {
    const kpi = this.selectedKpi ? this.getSelectedKpi() : null;
    const features = this.allPoints
      .filter((p) => {
        if (!kpi) {
          return true;
        }
        const kpiValue = p.kpis ? p.kpis[this.selectedKpi!] : null;
        const legendItemId = this.getKpiLegendItemId(kpi, kpiValue ?? null);
        // show only when the associated legend item is toggled
        // defaults to true if legendItemId does not exist
        return legendItemId !== null
          ? this.mapLegend.find((item) => item.id === legendItemId)?.toggled !== false
          : true;
      })
      .map(
        (p) =>
          new Feature({
            geometry: new OlPoint(fromLonLat([p.lng, p.lat])),
            id: p.id,
            kpis: p.kpis ?? {},
            color: this.pointColor(p),
          })
      );

    this.mapSource.clear(true);
    this.mapSource.addFeatures(features);

    if (shouldFit) {
      setTimeout(() => this.mapComponent?.fit());
    }
  }

  toggleLegendState(legendItemId: string) {
    const legendItemRef = this.mapLegend.find((item) => item.id === legendItemId);
    if (legendItemRef) {
      legendItemRef.toggled = !legendItemRef.toggled;
    }
    this.rebuildSource();
  }

  reset() {
    this.allPoints = [];
    this.mapSource.clear(true);
    this.hasNonGeocodedPds = false;
    this.isLoaded = false;
  }

  fetchMap(shouldFit = false) {
    this.reset();

    const granularity = this.periodService.granularity;
    if (
      this.filterService.hasPerimeter() &&
      this.selectedKpi &&
      this.periodService.from &&
      isKpiDisplayableForGranularity(this.getSelectedKpi(), granularity)
    ) {
      this.api
        .fetchPdsCount(this.filterService.getPerimeter())
        .pipe(
          tap((count: number) => (this.isHeavy = count > this.heavyMapThreshold)),
          switchMap(() =>
            this.isHeavy
              ? this.dialog.open(ModalConfirmHeavyMapLoadingComponent, { maxWidth: '700px' }).afterClosed()
              : of(true)
          ),
          filter((confirmed) => !!confirmed),
          tap(() => this.isLoading++),
          switchMap(() =>
            this.api
              .fetchKpiMap(
                this.filterService.getPerimeter(),
                this.selectedKpi!,
                this.periodService.from!,
                this.periodService.granularity
              )
              .pipe(
                this.messenger.showFriendlyErrorMessage(),
                tap((data: MapResponse) => {
                  this.hasNonGeocodedPds = data.non_geocoded_pds > 0;
                  this.allPoints = data.pds;
                  this.rebuildSource(shouldFit);
                }),
                finalize(() => {
                  this.isLoaded = true;
                  this.isLoading--;
                })
              )
          )
        )
        .subscribe();
    }
  }

  openContractKpiHelp() {
    this.dialog.open(ModalHelpContractConfigComponent, {
      maxWidth: '900px',
      data: {
        kpis: this.kpis,
        techno: this.filterService.getTechno(),
      },
    });
  }

  clearSelectionOnFilterChange() {
    this.filterService.filter$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.selectedPdsId = null;
          this.searchVersion++;
          this.mapComponent?.closePopup();
        })
      )
      .subscribe();
  }
}
